package com.evoapps.evoshift.domain.usecase.finance

import com.evoapps.evoshift.domain.model.BreakPolicy
import com.evoapps.evoshift.domain.model.ClockReviewSettings
import com.evoapps.evoshift.domain.model.payableTimes
import com.evoapps.evoshift.domain.model.workedHours

import com.evoapps.evoshift.domain.model.DailyCosts
import com.evoapps.evoshift.domain.model.DailyEfficiency
import com.evoapps.evoshift.domain.model.DailyLaborStats
import com.evoapps.evoshift.domain.model.DailyRecord
import com.evoapps.evoshift.domain.model.DailyRecordIds
import com.evoapps.evoshift.domain.model.DailyRevenue
import com.evoapps.evoshift.domain.model.DayClosedException
import com.evoapps.evoshift.domain.model.FixedCost
import com.evoapps.evoshift.domain.model.LaborSummary
import com.evoapps.evoshift.domain.model.PersonLabour
import com.evoapps.evoshift.domain.model.RevenueSource
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.ShiftFinancials
import com.evoapps.evoshift.domain.model.SystemContractType
import com.evoapps.evoshift.domain.model.WorkplaceFinancials
import com.evoapps.evoshift.domain.model.workerId
import com.evoapps.evoshift.domain.model.labourShifts
import com.evoapps.evoshift.domain.repository.FinancialRepository
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.budget.LabourInclusion
import com.evoapps.evoshift.domain.budget.LabourInclusionReader
import com.evoapps.evoshift.domain.budget.LabourPeople
import com.evoapps.evoshift.domain.budget.MissingShiftCosts
import com.evoapps.evoshift.domain.budget.OpenShiftRate
import com.evoapps.evoshift.domain.budget.OpenShiftRates
import com.evoapps.evoshift.domain.budget.ShiftCostEstimate
import com.evoapps.evoshift.domain.budget.isUnfilledDemand
import com.evoapps.evoshift.domain.market.payroll.OpenShiftCostSetting
import com.evoapps.evoshift.domain.model.Capability
import com.evoapps.evoshift.domain.usecase.auth.CallerCapabilities
import com.evoapps.evoshift.domain.usecase.auth.allows
import com.evoapps.evoshift.domain.util.calculateDurationHours
import com.evoapps.evoshift.domain.model.isPaidFor
import kotlinx.coroutines.async
import kotlinx.coroutines.coroutineScope
import kotlinx.datetime.LocalDate
import kotlinx.datetime.number

/** Everything a month's daily records are computed from, loaded once (see [CalculateDailyRecordUseCase.load]). */
data class DailyRecordInputs(
    val workplaceFinancials: WorkplaceFinancials,
    val shifts: List<Shift>,
    val shiftFinancials: List<ShiftFinancials>,
    val fixedCosts: List<FixedCost>,
    /** Last year's records of the same month, by ISO date, for the year-over-year comparison. */
    val previousYear: Map<String, DailyRecord>,
    /** SH-116: which break time is not worked (the hours worked leave it out). */
    val breakPolicy: BreakPolicy = BreakPolicy(),
    /**
     * People whose shifts are not paid work here (an unpaid owner, SH-147 people confirmed as
     * working without pay, the owner without pay terms): no cost, and not "unpriced".
     */
    val unpaidUserIds: Set<String> = emptySet(),
    /**
     * SH-232: the shifts on days their people work without pay (a period on the employee
     * timeline): no cost, not "unpriced"; their hours count when the person counts.
     */
    val unpaidShiftIds: Set<String> = emptySet(),
    /** SH-134: whether the hours count from the planned start (payable times). */
    val clockReview: ClockReviewSettings = ClockReviewSettings(),
    /** SH-193: what the month's unassigned shifts are estimated at per hour in the plan ([OpenShiftRates]). */
    val openShiftRate: OpenShiftRate = OpenShiftRate.NONE,
    /**
     * SH-206: who counts in the month's labour cost, labour % and SPMH ([LabourInclusion]): the
     * excluded people's shifts add neither cost nor hours; those counted without a wage add their
     * hours at no cost.
     */
    val labourPeople: LabourPeople = LabourPeople.UNKNOWN
)

/** Manually entered values of a day (takings, plan override, comments) that a recomputation keeps. */
data class DailyRevenueInput(
    /** Gross takings of records from before SH-70; only kept, never used in a calculation. */
    val actualBrutto: Double? = null,
    /** The day's takings, net. */
    val actualNetto: Double? = null,
    val planNetto: Double? = null,
    val comments: String? = null,
    /** SH-123: where [actualNetto] came from (kept with it; null: typed before SH-123 or none). */
    val source: RevenueSource? = null,
    /** SH-123: when an import wrote [actualNetto]. */
    val importedAt: String? = null
) {
    /**
     * [netto] typed by hand (SH-123): marked MANUAL, so imports leave it alone from now on. The
     * same figure an import wrote (a dialog saved for its comment) keeps the import's marker.
     */
    fun withTypedTakings(netto: Double?): DailyRevenueInput = when {
        netto == null -> copy(actualNetto = null, source = null, importedAt = null)
        netto == actualNetto && source?.isImport == true -> this
        else -> copy(actualNetto = netto, source = RevenueSource.MANUAL, importedAt = null)
    }

    companion object {
        /** What a recomputation keeps of a stored record (null: nothing stored). */
        fun keptFrom(stored: DailyRecord?): DailyRevenueInput = DailyRevenueInput(
            actualBrutto = stored?.revenue?.realBrutto,
            actualNetto = stored?.revenue?.realNetto,
            planNetto = stored?.revenue?.planNetto,
            comments = stored?.revenue?.comments,
            source = stored?.revenue?.source,
            importedAt = stored?.revenue?.importedAt
        )
    }
}

/**
 * The daily record of one day. Every revenue figure is NET (SH-70): the takings, the weekday sales
 * targets, the fixed revenue; the commission is a share of the net revenue, and labour % and
 * sales per man-hour are measured against net revenue. (A gross figure from older records is kept
 * as stored but takes part in nothing.)
 *
 * Revenue: target from the workplace's weekday targets unless
 * overridden, fixed revenue spread over the month's days, commission, last year), labor cost and
 * hours per contract type from the shifts and their cost snapshots, and sales per man-hour.
 */
class CalculateDailyRecordUseCase(
    private val financialRepository: FinancialRepository,
    private val shiftRepository: ShiftRepository,
    private val workplaceRepository: WorkplaceRepository,
    /**
     * Pay terms for shifts without a usable cost snapshot, priced on the fly (MissingShiftCosts);
     * without it (or for those without VIEW_FINANCES) such shifts are counted as unpriced.
     */
    private val userRepository: UserRepository? = null,
    private val caller: CallerCapabilities? = null,
    /** SH-193: the team's average cost per hour from their pay terms, for a month with no priced shift yet. */
    private val teamCostRate: GetTeamCostRateUseCase? = null,
    /**
     * SH-210: the month's interviews; the paid ones count as shifts of their participants (cost
     * and hours, priced from their pay terms: [com.evoapps.evoshift.domain.model.Interview.labourShifts]).
     */
    private val interviewRepository: com.evoapps.evoshift.domain.repository.InterviewRepository? = null,
    /** SH-206: who counts in the labour figures; without it (or without VIEW_FINANCES) the defaults of before apply. */
    private val labourInclusion: LabourInclusionReader? = null
) {
    suspend operator fun invoke(
        workplaceId: String,
        rosterId: String,
        date: String,
        actualBrutto: Double?,
        actualNetto: Double?,
        planNetto: Double? = null,
        comments: String? = null
    ): Result<DailyRecord> = runCatching {
        val inputs = load(workplaceId, rosterId).getOrThrow()
        // SH-123: typed takings are the person's (MANUAL) unless they are the imported figure unchanged.
        val stored = financialRepository.getDailyRecordsForRoster(rosterId).getOrDefault(emptyList()).firstOrNull { it.date == date }
        val input = DailyRevenueInput.keptFrom(stored)
            .copy(actualBrutto = actualBrutto, planNetto = planNetto, comments = comments)
            .withTypedTakings(actualNetto)
        compute(workplaceId, rosterId, date, inputs, input)
    }

    /**
     * Loads a roster month's inputs with one read per collection (not one per day), all at the
     * same time. [withRosterData] false skips the month's shifts, their cost snapshots and its
     * costs: a month being created has none the caller does not already hold (CreateRosterUseCase).
     */
    suspend fun load(workplaceId: String, rosterId: String, withRosterData: Boolean = true): Result<DailyRecordInputs> = runCatching {
        val (year, month) = rosterId.substringAfter("${workplaceId}_").split("_").let { it[0].toInt() to it[1].toInt() }
        val previousYearRosterId = RosterIds.of(workplaceId, year - 1, month)
        val previousMonthRosterId = if (month == 1) RosterIds.of(workplaceId, year - 1, 12) else RosterIds.of(workplaceId, year, month - 1)
        coroutineScope {
            val workplaceFinancials = async { workplaceRepository.getWorkplaceFinancials(workplaceId).getOrThrow() }
            val shifts = async { if (withRosterData) shiftRepository.getShiftsForRoster(rosterId).getOrThrow() else emptyList() }
            // SH-210: paid interviews as their participants' shifts, in memory only (never saved).
            val interviews = async {
                if (withRosterData) interviewRepository?.getForRoster(rosterId)?.getOrNull()?.labourShifts().orEmpty() else emptyList()
            }
            val shiftFinancials = async { if (withRosterData) financialRepository.getShiftFinancialsForRoster(rosterId).getOrThrow() else emptyList() }
            val fixedCosts = async { if (withRosterData) financialRepository.getFixedCosts(rosterId).getOrThrow() else emptyList() }
            val previousYear = async {
                financialRepository.getDailyRecordsForRoster(previousYearRosterId).getOrDefault(emptyList()).associateBy { it.date }
            }
            val workplace = async { workplaceRepository.getWorkplace(workplaceId).getOrNull() }
            // SH-193: last month's cost per hour worked, for the unassigned shifts' estimate (none: no history).
            val previousMonth = async {
                if (withRosterData) financialRepository.getDailyRecordsForRoster(previousMonthRosterId).getOrDefault(emptyList()) else emptyList()
            }
            val wp = workplace.await()
            val monthShifts = shifts.await() + interviews.await()
            // SH-232: paid work by the shift's own day; the people with no paid day this month at all.
            val monthId = LabourInclusion.monthOf(year, month)
            val unpaidShifts = wp?.let { w -> monthShifts.filter { s -> s.workerId?.let { !w.isPaidFor(it, s) } == true }.map { it.id }.toSet() }.orEmpty()
            val unpaid = wp?.let { w -> monthShifts.mapNotNull { it.workerId }.distinct().filterNot { w.isPaidIn(it, monthId) }.toSet() }.orEmpty()
            val stored = shiftFinancials.await()
            // Shifts without a usable snapshot: priced from their people's pay terms where readable.
            val missing = MissingShiftCosts.unpriced(monthShifts.filter { it.id !in unpaidShifts }, stored.associateBy { it.shiftId }) { it !in unpaid }
            val users = userRepository
            var ownerWithoutTerms = emptySet<String>()
            var engagements = emptyMap<String, com.evoapps.evoshift.domain.model.FinancialEngagement>()
            val readsMoney = users != null && wp != null && caller.allows(workplaceId, Capability.VIEW_FINANCES)
            val snapshots = if (missing.isEmpty() || !readsMoney) {
                stored
            } else {
                engagements = MissingShiftCosts.engagementsOf(users!!, workplaceId, missing.mapNotNull { it.workerId })
                ownerWithoutTerms = MissingShiftCosts.ownerWithoutTerms(wp!!.ownerId, missing, engagements)
                MissingShiftCosts.completed(stored, missing, monthShifts, engagements, wp.settings)
            }
            val unpaidIds = unpaid + ownerWithoutTerms
            // SH-206: who counts in the month's figures (the defaults of before when it cannot be read).
            val reader = labourInclusion
            val labourPeople = if (readsMoney && reader != null) {
                reader.people(wp!!, monthShifts.mapNotNull { it.workerId }, LabourInclusion.monthOf(year, month), engagements)
                    .getOrDefault(LabourPeople.UNKNOWN)
            } else LabourPeople.UNKNOWN
            // SH-193: this month's assigned shifts' average (the team's pay terms before any snapshot);
            // SH-206: only of the people who count, at their own cost.
            val notAveraged = unpaidIds + labourPeople.excluded + labourPeople.countedWithoutWage
            val thisMonth = ShiftCostEstimate.of(monthShifts, snapshots, notAveraged).averageCostPerHour
                ?: if (monthShifts.any { it.isUnfilledDemand }) teamCostRate?.invoke(rosterId)?.getOrNull() else null
            val openShiftRate = OpenShiftRates.resolve(
                wp?.settings?.payroll?.openShiftCost ?: OpenShiftCostSetting(),
                OpenShiftRates.ofMonth(previousMonth.await()),
                thisMonth
            )
            DailyRecordInputs(
                openShiftRate = openShiftRate,
                breakPolicy = wp?.settings?.breakPolicy ?: BreakPolicy(),
                clockReview = wp?.settings?.clockReview ?: ClockReviewSettings(),
                workplaceFinancials = workplaceFinancials.await(),
                shifts = monthShifts,
                shiftFinancials = snapshots,
                fixedCosts = fixedCosts.await(),
                previousYear = previousYear.await(),
                unpaidUserIds = unpaidIds,
                unpaidShiftIds = unpaidShifts,
                labourPeople = labourPeople
            )
        }
    }

    /**
     * [date]'s record recomputed from the month's current data, keeping what was entered by hand
     * (the stored record's takings, plan and comments, if it is stored) with [change] applied, and
     * its lock. Nothing is saved. A closed day is final: fails with [DayClosedException].
     */
    suspend fun recompute(
        workplaceId: String,
        rosterId: String,
        date: String,
        change: (DailyRevenueInput) -> DailyRevenueInput
    ): Result<DailyRecord> = runCatching {
        // The month's records (a query the rules allow, cached offline), not a single document read
        // that fails for a day never stored.
        val stored = financialRepository.getDailyRecordsForRoster(rosterId).getOrThrow().firstOrNull { it.date == date }
        if (stored?.isLocked == true) throw DayClosedException(date)
        val kept = DailyRevenueInput.keptFrom(stored)
        val inputs = load(workplaceId, rosterId).getOrThrow()
        compute(workplaceId, rosterId, date, inputs, change(kept))
    }

    /** Pure computation of one day from preloaded [inputs]. */
    fun compute(
        workplaceId: String,
        rosterId: String,
        date: String,
        inputs: DailyRecordInputs,
        revenueInput: DailyRevenueInput = DailyRevenueInput()
    ): DailyRecord {
        val day = LocalDate.parse(date)
        val wf = inputs.workplaceFinancials
        val target = revenueInput.planNetto ?: wf.salesTargetFor(day.dayOfWeek) ?: 0.0
        // The fixed monthly revenue counts every day: an equal share, the last day the remainder.
        val fixedRevenue = com.evoapps.evoshift.domain.model.LabourShare.fixedRevenueShare(wf.fixedRevenueMonthly, day)
        val previous = inputs.previousYear[LocalDate(day.year - 1, day.month, minOf(day.day, daysInMonth(LocalDate(day.year - 1, day.month.number, 1)))).toString()]

        val revenue = DailyRevenue(
            planNetto = target,
            realBrutto = revenueInput.actualBrutto,
            realNetto = revenueInput.actualNetto,
            previousYearBrutto = previous?.revenue?.realBrutto,
            previousYearNetto = previous?.revenue?.realNetto,
            fixedRevenue = fixedRevenue,
            commissionPercent = wf.commissionPercent,
            otherRevenue = inputs.fixedCosts.filter { it.isRevenue }.sumOf { it.amountOn(date) },
            comments = revenueInput.comments,
            source = revenueInput.source.takeIf { revenueInput.actualNetto != null },
            importedAt = revenueInput.importedAt.takeIf { revenueInput.actualNetto != null }
        )

        val financialsByShift = inputs.shiftFinancials.filter { it.date == date }.associateBy { it.shiftId }
        val plannedByContract = mutableMapOf<SystemContractType, Double>()
        val actualByContract = mutableMapOf<SystemContractType, Double>()
        var plannedHours = 0.0
        var actualHours = 0.0
        var plannedCost = 0.0
        var actualCost = 0.0
        // Paid shifts nobody could price (no snapshot, no readable pay terms): said, not hidden.
        val unpriced = mutableListOf<Shift>()
        // SH-152: the same figures per person, for the explanation of the day's labour cost.
        val plannedByPerson = mutableMapOf<String, PersonLabour>()
        val actualByPerson = mutableMapOf<String, PersonLabour>()
        val excludedByPerson = mutableMapOf<String, PersonLabour>()
        fun MutableMap<String, PersonLabour>.add(shift: Shift, cost: Double, hours: Double) {
            val id = shift.workerId ?: return
            this[id] = (this[id] ?: PersonLabour()) + PersonLabour(cost, hours, 1)
        }
        // Revenue is always net (SH-70): the day counts as traded once its net takings are in.
        val dayTraded = revenueInput.actualNetto != null

        // SH-193: unassigned shifts (demand nobody fills yet) count in the plan: their hours always,
        // their cost at the workplace's estimated rate when there is one. Never in the actual.
        var openShifts = 0
        var openHours = 0.0
        inputs.shifts.filter { it.date == date && !it.notWorked }.forEach { shift ->
            if (shift.isUnfilledDemand) {
                openShifts += 1
                openHours += calculateDurationHours(shift.plannedTime.start, shift.plannedTime.end)
                return@forEach
            }
            val worker = shift.workerId
            // SH-206: a person who does not count adds neither cost nor hours (nor a missing-pay warning).
            if (worker != null && !inputs.labourPeople.counts(worker)) {
                excludedByPerson.add(shift, 0.0, calculateDurationHours(shift.plannedTime.start, shift.plannedTime.end))
                return@forEach
            }
            // SH-206: someone counted who works without a wage: their hours, at no cost.
            // SH-232: and a shift on a day they work without pay.
            val withoutWage = worker != null && (worker in inputs.labourPeople.countedWithoutWage || shift.id in inputs.unpaidShiftIds)
            // A snapshot of someone who no longer works the shift is stale (SH-12): not counted.
            // SH-128: who worked it (recorded afterwards on an open shift) is who it is priced for.
            val financial = financialsByShift[shift.id]
                ?.takeIf { it.userId == null || it.userId == shift.workerId }
                ?.takeIf { !withoutWage }
            if (financial == null && !withoutWage) {
                if (worker != null && worker !in inputs.unpaidUserIds) unpriced += shift
                return@forEach
            }
            // Older snapshots hold the free-text contract ("Zlecenie"); unknown counts as employment.
            val type = financial?.let {
                SystemContractType.fromId(it.ratesSnapshot.contractType)
                    .takeIf { t -> t != SystemContractType.NOT_SET } ?: SystemContractType.STANDARD_EMPLOYMENT
            }
            fun MutableMap<SystemContractType, Double>.addCost(cost: Double) {
                if (type != null) this[type] = (this[type] ?: 0.0) + cost
            }
            val shiftPlannedHours = calculateDurationHours(shift.plannedTime.start, shift.plannedTime.end)
            val shiftPlannedCost = financial?.costs?.employerCost ?: 0.0
            // SH-128: an open shift somebody covered was not in the plan's cost: only the actual one.
            if (shift.userId != null || shift.actualUserId == null) {
                plannedByContract.addCost(shiftPlannedCost)
                plannedCost += shiftPlannedCost
                plannedHours += shiftPlannedHours
                plannedByPerson.add(shift, shiftPlannedCost, shiftPlannedHours)
            }

            val clocked = shift.payableTimes(inputs.clockReview)
            if (clocked != null) {
                val shiftActualHours = shift.workedHours(inputs.breakPolicy, inputs.clockReview) ?: calculateDurationHours(clocked.start, clocked.end)
                // Priced from the clocked times; older snapshots: the hourly cost × hours worked.
                val shiftActualCost = financial?.costs?.actualEmployerCost
                    ?: if (shiftPlannedHours > 0) shiftPlannedCost / shiftPlannedHours * shiftActualHours else shiftPlannedCost
                actualByContract.addCost(shiftActualCost)
                actualCost += shiftActualCost
                actualHours += shiftActualHours
                actualByPerson.add(shift, shiftActualCost, shiftActualHours)
            } else if (dayTraded) {
                // Nobody clocked (staff without the app): once the takings are in, the shift counts
                // as worked as planned, as the payroll pays it (SH-28: labour % was 0).
                actualByContract.addCost(shiftPlannedCost)
                actualCost += shiftPlannedCost
                actualHours += shiftPlannedHours
                actualByPerson.add(shift, shiftPlannedCost, shiftPlannedHours)
            }
        }

        val openRate = inputs.openShiftRate.perHour.takeIf { openShifts > 0 }
        val openCost = openRate?.let { it * openHours } ?: 0.0
        plannedHours += openHours
        plannedCost += openCost

        val actualNetto = revenueInput.actualNetto ?: 0.0
        return DailyRecord(
            id = DailyRecordIds.of(workplaceId, date),
            rosterId = rosterId,
            workplaceId = workplaceId,
            date = date,
            revenue = revenue,
            laborStats = DailyLaborStats(
                planned = LaborSummary(plannedCost, plannedHours, if (target > 0) plannedCost / target * 100 else 0.0),
                actual = LaborSummary(actualCost, actualHours, if (actualNetto > 0) actualCost / actualNetto * 100 else 0.0)
            ),
            costs = DailyCosts(
                plannedHours = plannedHours,
                actualHours = actualHours,
                plannedByContractType = plannedByContract,
                actualByContractType = actualByContract,
                // SH-87: the ledger's costs of the day; its revenue items count as the day's income.
                // An item covering several days counts an equal part on each (FixedCost.amountOn).
                otherCosts = inputs.fixedCosts.filter { !it.isRevenue }.sumOf { it.amountOn(date) },
                unpricedShifts = unpriced.size,
                unpricedHours = unpriced.sumOf { calculateDurationHours(it.plannedTime.start, it.plannedTime.end) },
                unpricedUserIds = unpriced.mapNotNull { it.workerId }.distinct(),
                plannedByPerson = plannedByPerson,
                actualByPerson = actualByPerson,
                openShifts = openShifts,
                openHours = openHours,
                openCost = openCost,
                openCostPerHour = openRate,
                openRateSource = inputs.openShiftRate.source.name.takeIf { openShifts > 0 },
                otherCostItems = ledgerItemsOn(inputs.fixedCosts.filter { !it.isRevenue }, date),
                otherRevenueItems = ledgerItemsOn(inputs.fixedCosts.filter { it.isRevenue }, date),
                excludedByPerson = excludedByPerson,
                unpricedByPerson = unpriced.groupBy { it.workerId!! }.mapValues { (_, shifts) ->
                    PersonLabour(0.0, shifts.sumOf { calculateDurationHours(it.plannedTime.start, it.plannedTime.end) }, shifts.size)
                }
            ),
            // SH-191: null until the takings are in (it was 0 for a clocked day without them).
            efficiency = DailyEfficiency(spmhReal = com.evoapps.evoshift.domain.model.Spmh.actual(revenueInput.actualNetto, actualHours, plannedHours - openHours))
        )
    }

    /** SH-195: [items]' parts on [date] as the record counts them, for the breakdown (items with nothing on the day left out). */
    private fun ledgerItemsOn(items: List<FixedCost>, date: String): List<com.evoapps.evoshift.domain.model.LedgerDayItem> =
        items.mapNotNull { item ->
            item.amountOn(date).takeIf { it != 0.0 }?.let { com.evoapps.evoshift.domain.model.LedgerDayItem(item.name, it, item.spreadsOverMonth) }
        }

    private fun daysInMonth(date: LocalDate): Int {
        val first = LocalDate(date.year, date.month, 1)
        val next = if (date.month.number == 12) LocalDate(date.year + 1, 1, 1) else LocalDate(date.year, date.month.number + 1, 1)
        return (next.toEpochDays() - first.toEpochDays()).toInt()
    }
}
